import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { EDIT_PAGE_CONNECTION_ORDER_CREATE_GENERIC_ERROR } from 'src/app/shared/constants/errorMessages';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { isAbortError } from 'src/app/utils/isAbortError';
import { log } from 'src/app/utils/logging';
import {
  actionUpdateCartLineItem,
  actionUpdateCartOrderId,
} from 'src/app/v1/state/modules/session/cart';
import { actionUpdateIsJobPosted } from 'src/app/v1/state/modules/session/isJobPosted';
import { actionUpdateJob } from 'src/app/v1/state/modules/session/job';
import { actionUpdateJobOrderId } from 'src/app/v1/state/modules/session/jobOrderId';
import { actionUpdateOrderNumber } from 'src/app/v1/state/modules/tealium/orderNumber';
import type { CartLineItem } from 'src/app/v1/state/types/cart';
import { useDraft } from 'src/app/v1/views/hooks/useDraft';
import { useEditConfirmInvoiceOrder } from 'src/app/v1/views/hooks/useEditConfirmInvoiceOrder';
import { useEditJob } from 'src/app/v1/views/hooks/useEditJob';
import { useGenerateJobIdempotencyKey } from 'src/app/v1/views/hooks/useGenerateJobIdempotencyKey';
import type { AdProduct, AdProductUpgrade } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

import { useCallbacksForCreateProductSetForAdEditing } from '../useCallbacksForCreateProductSetForAdEditing';
import { useCreateOrderWithProductSet } from '../useCreateOrderWithProductSet';
import { useCreateProductSet } from '../useCreateProductSet';

export const useEditSubmitOrder = () => {
  const {
    createProductSetRequest,
    seekAdvertiserId,
    sourceJobId,
    cart,
    billToAgent,
  } = useSelector(
    (state: AppState) => ({
      createProductSetRequest: state.session.createProductSetRequest,
      seekAdvertiserId: state.session.seekAdvertiserId,
      sourceJobId: state.session.sourceJobId,
      cart: state.session.cart,
      billToAgent: state.session.billToAgent,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const editJob = useEditJob();
  const callbacksForCreateProductSet =
    useCallbacksForCreateProductSetForAdEditing();
  const createProductSet = useCreateProductSet(callbacksForCreateProductSet);
  const createOrderWithProductSet =
    useCreateOrderWithProductSet(createProductSet);
  const confirmInvoiceOrder = useEditConfirmInvoiceOrder();
  const { saveDraft } = useDraft();
  const idempotencyKey = useGenerateJobIdempotencyKey();
  const { assignJobIdAndRevisionToParentProduct, createAndConfirmOrder } =
    useShoppingCart();

  const publish = async () => {
    await saveDraft(false);

    const revisionId = await editJob(idempotencyKey);

    if (sourceJobId) {
      dispatch(
        actionUpdateJob({
          id: sourceJobId,
          idempotencyKey,
          revisionId,
        }),
      );

      await assignJobIdAndRevisionToParentProduct({
        jobId: sourceJobId,
        revisionId,
      });

      const response = await createAndConfirmOrder({
        billToAgent,
        idempotencyKey,
      });

      if (response.result === 'error') {
        log.error(
          'useEditSubmitOrder | publish | error creating or confirming order',
          {
            error: response.error,
          },
        );
        throw new Error(EDIT_PAGE_CONNECTION_ORDER_CREATE_GENERIC_ERROR);
      }

      dispatch(actionUpdateOrderNumber(response.orderId));

      await saveDraft(true);

      dispatch(actionUpdateIsJobPosted(true));
    }
  };

  const saveDraftAndCreateOrder = useCallback(async () => {
    await saveDraft(false);
    const revisionId = await editJob(idempotencyKey);
    dispatch(
      actionUpdateJob({
        id: sourceJobId,
        idempotencyKey,
        revisionId,
      }),
    );

    const createProductSetResponse = await createProductSetRequest;
    const productSetId = createProductSetResponse?.productSet?.id;
    if (!productSetId || createProductSetResponse?.error) {
      log.error('Edit | createProductSetForOrder', {
        seekAdvertiserId,
        productSetId,
        error: createProductSetResponse?.error,
      });
      throw new Error(EDIT_PAGE_CONNECTION_ORDER_CREATE_GENERIC_ERROR);
    }

    const productSetItems = createProductSetResponse.productSet?.items as Array<
      AdProduct | AdProductUpgrade
    >;

    const hasCartItemInProductSet = (cartProductId: string) =>
      productSetItems.find((productItem) => productItem?.id === cartProductId);

    const cartLineItems: CartLineItem[] = cart.lineItems.map((item) => {
      if (!Boolean(hasCartItemInProductSet(item.productId))) {
        log.error('Edit | cartItem not present in the product set', {
          seekAdvertiserId,
          productSetId,
          cartItem: item,
        });
        throw new Error(EDIT_PAGE_CONNECTION_ORDER_CREATE_GENERIC_ERROR);
      }

      const updatedLineItem = {
        ...item,
        productSetId,
        revisionId,
      };
      dispatch(actionUpdateCartLineItem(updatedLineItem));
      return updatedLineItem;
    });

    const data = await createOrderWithProductSet(cartLineItems, productSetId);

    dispatch(actionUpdateCartOrderId(data.orderId ?? null));
    dispatch(actionUpdateJobOrderId(data.orderId ?? ''));
    dispatch(actionUpdateOrderNumber(data.orderNumber ?? null));

    if (data.error && !isAbortError(data.error)) {
      log.error('Edit | createOrderWithProductSet', {
        error: data.error,
        seekAdvertiserId,
        productSetId,
        cartItems: cartLineItems,
      });
      throw new Error(EDIT_PAGE_CONNECTION_ORDER_CREATE_GENERIC_ERROR);
    }

    return { orderId: data.orderId };
  }, [
    cart.lineItems,
    createOrderWithProductSet,
    createProductSetRequest,
    dispatch,
    editJob,
    idempotencyKey,
    saveDraft,
    seekAdvertiserId,
    sourceJobId,
  ]);

  const confirmOrderAndCloseDraft = useCallback(
    async (orderId: string) => {
      await confirmInvoiceOrder(idempotencyKey, orderId);

      await saveDraft(true);
      dispatch(actionUpdateIsJobPosted(true));
    },
    [idempotencyKey, confirmInvoiceOrder, saveDraft, dispatch],
  );

  return {
    confirmOrderAndCloseDraft,
    publish,
    saveDraftAndCreateOrder,
  };
};
